import React, { useState, useContext, useEffect } from 'react';
import {AuthContext} from "../auth/AuthContext";
import {createFetchRequest} from "../util/fetcher";

interface ShowImageUploadProps {
    article?: string;
    onUpload: () => void;
}

export const ShowImageUpload: React.FunctionComponent<ShowImageUploadProps> = ({ article, onUpload}) => {
    const { sessionId } = useContext(AuthContext);
    const [roles, setRoles] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

    useEffect(() => {
        if (loading) {
            return;
        }
        if (roles.length) {
            return;
        }
        setLoading(true);
        void createFetchRequest('api/user/permissions', sessionId)
            .then( resp => resp.json() )
            .then( roles => setRoles( roles ))
            .finally( () => setLoading( false ));
    }, [loading, roles, sessionId])

    if (!article) {
        return null;
    }
    if (!roles.includes('Admin')) {
        return null;
    }

    const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.currentTarget.classList.add('drag-active');
        if (!e.dataTransfer.types.includes('Files')) {
            e.currentTarget.classList.add('forbidden');
            return false;
        }
        return true;
    }

    const onDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
        e.currentTarget.classList.remove('drag-active', 'forbidden');
    }

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const outcome: File[] = [];
        if (e.dataTransfer.items) {
            for (let file of Array.from( e.dataTransfer.items )) {
                if (file.kind === 'file') {
                    const ct = file.getAsFile();
                    if (!ct) {
                        continue;
                    }
                    outcome.push(ct);
                }
            }
        } else if (e.dataTransfer.files) {
            Array.from(e.dataTransfer.files).forEach( f => outcome.push(f));
        }
        setFilesToUpload(outcome);
    }

    const uploadFiles = async (e: React.SyntheticEvent) => {
        if (!filesToUpload) {
            return;
        }
        e.currentTarget.setAttribute('disabled', 'true');
        const uploadFile = async (f: File): Promise<void> => {
            const body = await f.arrayBuffer();
            await createFetchRequest(`api/media/upload/${article}/${f.type}`, sessionId, "POST", body );
        }
        let promise = null;
        for (let file of filesToUpload) {
            if (promise === null) {
                promise = uploadFile( file );
                continue;
            }
            promise.then( () => uploadFile( file ));
        }
        await promise;
        setFilesToUpload([]);
        onUpload();
    }

    const attachFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (!e.currentTarget.files) {
            return;
        }
        setFilesToUpload( Array.from( e.currentTarget.files ));
    }

    const hasFilesToUpload = filesToUpload.length > 0;

    return (
        <>
            <div className="drop-zone" onDragOver={onDragEnter} onDragEnd={onDragEnd} onDrop={onDrop}>
                { hasFilesToUpload && filesToUpload.map( f => (<span className="fto">{ f.name }</span> ) ) }
                { hasFilesToUpload && <button type="button" onClick={uploadFiles}>Upload</button> }
                <span>🎯 Drop files here!</span>
                <input type="file" multiple={true} accept={"image/*"} onChange={attachFiles} />
            </div>
        </>
    );
}